import { reaction, runInAction, when } from 'mobx';
import { salvarDisponibilidadeEscaninhos } from '../utils/DisponibilidadeEscaninhos';
import ControleGabineteApi from '../api/ControleGabineteApi';
import { ControleGabinete, TipoControle } from '../modelo';
import UsuarioStore from '../store/UsuarioStore';
import ControleGabineteStore from '../store/ControleGabineteStore';
import MensagemStore from '../store/MensagemStore';
import { MapeadorControleGabinete } from './mapeadores/MapeadorControleGabinete';

class ControleGabineteService {

    private mapeadorControleGabinete = new MapeadorControleGabinete();
    private atualizacaoEmAndamento?: Promise<ControleGabinete[] | undefined>;
    private atualizacaoEmAndamentoDesde = 0;
    private readonly lockMaxMs = 12000;

    // Atualizações disparadas pelo envelope (ALTERACAO_CONTROLE_GABINETE) são agrupadas nesta janela:
    // em rajadas (ex.: milhares de movimentações seguidas) resultam em poucas recargas do kanban.
    private readonly janelaAtualizacaoSegundoPlanoMs = 2000;
    private timerAtualizacaoSegundoPlano?: ReturnType<typeof setTimeout>;
    private atualizacaoSegundoPlanoEmAndamento = false;
    private atualizacaoSegundoPlanoPendente = false;
    // Recarga que ficou retida porque o usuário estava interagindo com o Kanban.
    private atualizacaoSegundoPlanoRetida = false;

    constructor() {
        reaction(
            () => ControleGabineteStore.atualizacoesPausadas,
            (pausada) => {
                if (!pausada && this.atualizacaoSegundoPlanoRetida) {
                    this.atualizacaoSegundoPlanoRetida = false;
                    this.atualizarControlesEmSegundoPlano();
                }
            }
        );
    }

    private reterAtualizacaoSegundoPlano() {
        this.atualizacaoSegundoPlanoRetida = true;
        ControleGabineteStore.setControlesComAtualizacaoPendente(true);
    }

    private async aguardarLocalizacaoUsuario(maxWaitMs: number = 15000): Promise<boolean> {
        if (UsuarioStore?.localizacao?.siglaOrgaoJulgador) {
            return true;
        }

        try {
            await when(
                () => UsuarioStore?.localizacao?.siglaOrgaoJulgador !== undefined,
                { timeout: maxWaitMs }
            );
            return true;
        } catch {
            return false;
        }
    }

    private async comTimeout<T>(promise: Promise<T>, timeoutMs: number, mensagem: string): Promise<T> {
        let timeoutId: ReturnType<typeof setTimeout> | undefined;

        const timeoutPromise = new Promise<T>((_, reject) => {
            timeoutId = setTimeout(() => reject(new Error(mensagem)), timeoutMs);
        });

        try {
            return await Promise.race([promise, timeoutPromise]);
        } finally {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        }
    }

    atualizarControlesGabinete = async (numero?: number): Promise<ControleGabinete[] | undefined> => {
        return this.atualizarControlesComLock(numero, false);
    }

    /**
     * Agenda uma recarga silenciosa dos controles (sem loading, sem toast de erro e sem trocar as
     * instâncias da store quando nada mudou). Chamadas dentro da janela são agrupadas numa só, e
     * chamadas durante uma recarga em andamento geram exatamente uma recarga posterior.
     */
    agendarAtualizacaoControles = () => {
        if (this.timerAtualizacaoSegundoPlano) {
            return;
        }

        this.timerAtualizacaoSegundoPlano = setTimeout(() => {
            this.timerAtualizacaoSegundoPlano = undefined;
            this.atualizarControlesEmSegundoPlano();
        }, this.janelaAtualizacaoSegundoPlanoMs);
    }

    private atualizarControlesEmSegundoPlano = async () => {
        // Trocar a estrutura do Kanban debaixo do cursor é pior que esperar: retém até o usuário parar.
        if (ControleGabineteStore.atualizacoesPausadas) {
            this.reterAtualizacaoSegundoPlano();
            return;
        }

        if (this.atualizacaoSegundoPlanoEmAndamento) {
            this.atualizacaoSegundoPlanoPendente = true;
            return;
        }

        ControleGabineteStore.setControlesComAtualizacaoPendente(false);

        this.atualizacaoSegundoPlanoEmAndamento = true;

        try {
            // Uma carga em andamento pode ter lido o kanban antes da alteração que nos disparou.
            if (this.atualizacaoEmAndamento) {
                await this.atualizacaoEmAndamento.catch(() => undefined);
            }
            await this.atualizarControlesComLock(undefined, true);
        } finally {
            this.atualizacaoSegundoPlanoEmAndamento = false;

            if (this.atualizacaoSegundoPlanoPendente) {
                this.atualizacaoSegundoPlanoPendente = false;
                this.agendarAtualizacaoControles();
            }
        }
    }

    private atualizarControlesComLock = async (numero: number | undefined, segundoPlano: boolean): Promise<ControleGabinete[] | undefined> => {
        if (this.atualizacaoEmAndamento && (Date.now() - this.atualizacaoEmAndamentoDesde) < this.lockMaxMs) {
            return this.atualizacaoEmAndamento;
        }

        if (this.atualizacaoEmAndamento && (Date.now() - this.atualizacaoEmAndamentoDesde) >= this.lockMaxMs) {
            this.atualizacaoEmAndamento = undefined;
        }

        this.atualizacaoEmAndamento = this.executarAtualizacaoControles(numero, 0, segundoPlano);
        this.atualizacaoEmAndamentoDesde = Date.now();

        try {
            return await this.atualizacaoEmAndamento;
        } finally {
            this.atualizacaoEmAndamento = undefined;
            this.atualizacaoEmAndamentoDesde = 0;
        }
    }

    private async executarAtualizacaoControles(numero?: number, tentativa: number = 0, segundoPlano: boolean = false): Promise<ControleGabinete[] | undefined> {
        if (numero) {
            // Mantem compatibilidade com chamadas de atualizacao pontual, forçando refresh do kanban.
        }

        // Em segundo plano os controles já exibidos continuam válidos: não alterna as flags de loading,
        // senão cada notificação do envelope faria a tela reagir como se estivesse carregando de novo.
        if (!segundoPlano) {
            runInAction(() => {
                ControleGabineteStore.controlesCarregados = false;
                ControleGabineteStore.carregandoControles = true;
            });
        }

        try {
            const localizacaoDisponivel = await this.comTimeout(
                this.aguardarLocalizacaoUsuario(),
                20000,
                'Tempo limite ao aguardar dados do usuário para carregar o controle de gabinete.'
            );

            if (!localizacaoDisponivel || !UsuarioStore?.localizacao?.siglaOrgaoJulgador) {
                if (segundoPlano) {
                    console.warn('Atualização em segundo plano dos controles ignorada: gabinete do usuário indisponível.');
                    return undefined;
                }
                MensagemStore.apresentarErro('Não foi possível identificar o gabinete do usuário para carregar os controles.');
                runInAction(() => {
                    ControleGabineteStore.controlesCarregados = false;
                    ControleGabineteStore.carregandoControles = false;
                });
                return undefined;
            }

            const controles = await this.comTimeout(
                this.getKanban(UsuarioStore.localizacao.siglaOrgaoJulgador, segundoPlano),
                20000,
                'Tempo limite ao carregar os controles de gabinete.'
            );
            if (!ControleGabineteStore.buscarPorTipo(TipoControle.REDATORDESIGNADO) && window.location.hostname.includes('.hml.')) {
                await this.criarControlesRedator();
            }
            return controles;
        } catch (error) {
            if (tentativa < 1) {
                return this.executarAtualizacaoControles(numero, tentativa + 1, segundoPlano);
            }

            console.error('Falha ao atualizar controles de gabinete.', error);
            if (segundoPlano) {
                return undefined;
            }
            MensagemStore.apresentarErro('Não foi possível carregar os controles de gabinete. Tente novamente.');
            runInAction(() => {
                ControleGabineteStore.controlesCarregados = false;
                ControleGabineteStore.carregandoControles = false;
            });
            return undefined;
        } finally {
            // Garantia final para nunca deixar a tela presa em loading.
            if (!segundoPlano && ControleGabineteStore.carregandoControles) {
                runInAction(() => {
                    ControleGabineteStore.carregandoControles = false;
                });
            }
        }
    }

    async criarControlesRedator() {
        const gabinete = UsuarioStore.gabinete;
        const controlesGabineteNovo = await ControleGabineteApi.criarControlesGabineteRedator(gabinete);
        const controles = this.mapeadorControleGabinete.mapearControlesGabinete(controlesGabineteNovo);
        controles.sort((card1, card2) => card1.numeroOrdem - card2.numeroOrdem);
        salvarDisponibilidadeEscaninhos(gabinete, controles);

        runInAction(() => {
            ControleGabineteStore.listaDeControles = controles;
            ControleGabineteStore.controlesCarregados = true;
            ControleGabineteStore.carregandoControles = false;
        })
    }

    async getKanban(gabinete, segundoPlano: boolean = false): Promise<ControleGabinete[]> {
        const kanban = await ControleGabineteApi.getKanban(gabinete);
        const controles = this.mapeadorControleGabinete.mapearControlesGabinete(kanban);
        controles.sort((card1, card2) => card1.numeroOrdem - card2.numeroOrdem);
        salvarDisponibilidadeEscaninhos(gabinete, controles);

        // Manter as instâncias atuais quando nada mudou evita invalidar as props de todos os cards.
        if (segundoPlano && ControleGabineteStore.controlesCarregados) {
            if (!this.controlesMudaram(controles)) {
                return ControleGabineteStore.listaDeControles;
            }

            // O usuário começou a interagir durante o fetch: descarta o resultado e refaz quando ele parar.
            if (ControleGabineteStore.atualizacoesPausadas) {
                this.reterAtualizacaoSegundoPlano();
                return ControleGabineteStore.listaDeControles;
            }
        }

        runInAction(() => {
            ControleGabineteStore.listaDeControles = controles;
            ControleGabineteStore.controlesCarregados = true;
            ControleGabineteStore.carregandoControles = false;
        })
        return controles;
    }

    private controlesMudaram(novos: ControleGabinete[]): boolean {
        return JSON.stringify(novos) !== JSON.stringify(ControleGabineteStore.listaDeControles.slice());
    }

    async criarControlesPadrao(gabinete) {
        const result = await ControleGabineteApi.criarControlesGabinete(gabinete);
        runInAction(() => {
            ControleGabineteStore.listaDeControles = result.map(r => ControleGabinete.criar(r));
        });
    }

    async salvarKanban(gabinete, cards) {
        const resultado = await ControleGabineteApi.salvarKanban(gabinete, cards);
        return resultado;
    }

    /**
     * Salva só a troca de nome (descrição) de um controle, sem depender das edições
     * pendentes (não salvas) que possam existir na tela de configuração — monta o payload
     * a partir da última lista persistida (`controles`), trocando apenas a descrição do
     * controle de número `numeroControle`.
     */
    async salvarNomeControle(gabinete: string, controles: ControleGabinete[], numeroControle: number, novaDescricao: string) {
        const cards = controles.map((it, index) => {
            const item: any = {
                descricao: it.numero === numeroControle ? novaDescricao : it.descricao,
                numero: it.numero,
                ativo: it.ativo,
                tipo: it.tipo,
                configControle: it.configControle
            };
            if (item.configControle && item.configControle.regrasDistribuicao) {
                item.configControle.regrasDistribuicao.forEach(rd => {
                    delete rd.regras['txt'];
                });
            }
            item.numeroOrdem = index;
            return item;
        });

        return await ControleGabineteApi.salvarKanban(gabinete, cards as any);
    }

    async enviarParaPauta(
        identificadorProcesso,
        orgaoJulgador,
        silenciarNotificacaoDeErro: boolean = false,
        signal?: AbortSignal
    ) {
        return await ControleGabineteApi.enviarParaPauta(identificadorProcesso, orgaoJulgador, silenciarNotificacaoDeErro, signal);
    }

    async enviarAptoJulgamento(identificadorProcesso, orgaoJulgador) {
        return await ControleGabineteApi.enviarAptoJulgamento(identificadorProcesso, orgaoJulgador);
    }

    async getTiposControles(): Promise<any[]> {
        return await ControleGabineteApi.getTiposControles() as any[];
    }
}

export default new ControleGabineteService();
